import { Close, TableSplit, View, WarningAltFilled } from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  Heading,
  Icon,
  IconComponentType,
  Tooltip,
} from 'vet-bones/design-system/components';

import { DataSourceIcon } from 'src/components/resourceDialog/DataSourceIcon';
import { getVgResourceCardTestId } from 'src/constants/testIds';
import { VgTableMetadata } from 'src/containers/ProjectNewResourceDialogV2/hooks/useVirtualGraphDialogState';
import { currentStardogVersionSelector } from 'src/selectors/currentStardogVersion';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

import styles from 'src/containers/ProjectNewResourceDialogV2/components/projectResourceCard.module.scss';

export type VirtualGraphResourceCardProps = {
  name: string;
  resourceIcon?: IconComponentType; // Optional, for backward compatibility
  dataSourceType?: string; // Data source type for icon
  metadata?: VgTableMetadata;
  validationError?: string;
  onViewPreview: () => void;
  onRemove: () => void;
};

export const VirtualGraphResourceCard: React.VFC<
  VirtualGraphResourceCardProps
> = ({
  name,
  resourceIcon,
  dataSourceType,
  metadata,
  validationError,
  onViewPreview,
  onRemove,
}) => {
  const stardogVersion = useReduxSelector(currentStardogVersionSelector);

  const description = metadata
    ? copy.components.projectResourceDialog.resourceTypePanelV2.vgResourceCard.description(
        metadata.fields.length,
        metadata.rowCount ?? 0,
        metadata.fields.map((f) => f.label).join(', ')
      )
    : null;

  const renderIcon = () => {
    if (dataSourceType) {
      return (
        <DataSourceIcon
          dataSourceType={dataSourceType}
          size={24}
          stardogVersion={stardogVersion}
        />
      );
    }
    if (resourceIcon) {
      return <Icon IconComponent={resourceIcon} size="md" />;
    }
    return <Icon IconComponent={TableSplit} size="md" />;
  };

  return (
    <div
      className={styles.projectResourceCard}
      data-testid={getVgResourceCardTestId(name)}
    >
      <div className={styles.header}>
        <div className={styles.icon}>{renderIcon()}</div>
        <div className={styles.content}>
          <Heading size="sm">{name}</Heading>
        </div>
        <div className={styles.actions}>
          {validationError ? (
            <Tooltip description={validationError}>
              <div
                className={styles.errorIcon}
                data-testid="vg-resource-card-error-icon"
              >
                <Icon IconComponent={WarningAltFilled} size="md" />
              </div>
            </Tooltip>
          ) : null}
          <Button
            icon={View}
            onClick={onViewPreview}
            size="xs"
            title={
              copy.components.projectResourceDialog.resourceTypePanelV2
                .projectResourceCard.viewPreview
            }
            variant="secondary-transparent-gray"
          />
          <Button
            icon={Close}
            onClick={onRemove}
            size="xs"
            title={
              copy.components.projectResourceDialog.resourceTypePanelV2
                .vgResourceCard.removeTable
            }
            variant="secondary-transparent-gray"
          />
        </div>
      </div>
      {description ? (
        <Body className={styles.description} size="md" title={description}>
          {description}
        </Body>
      ) : null}
    </div>
  );
};
